import { createSelector, createSelectorMemoized } from "../../../utils/createSelector.js";
import { gridSortedRowEntriesSelector } from "../sorting/gridSortingSelector.js";
import { gridColumnLookupSelector } from "../columns/gridColumnsSelector.js";
import { gridRowMaximumTreeDepthSelector, gridRowTreeSelector } from "../rows/gridRowsSelector.js";
/**
 * @category Filtering
 */ var gridFilterStateSelector = function(state) {
    return state.filter;
};
/**
 * Get the current filter model.
 * @category Filtering
 */ export var gridFilterModelSelector = createSelector(gridFilterStateSelector, function(filterState) {
    return filterState.filterModel;
});
/**
 * Get the current quick filter values.
 * @category Filtering
 */ export var gridQuickFilterValuesSelector = createSelector(gridFilterModelSelector, function(filterModel) {
    return filterModel.quickFilterValues;
});
/**
 * @category Visible rows
 * @ignore - do not document.
 */ export var gridVisibleRowsLookupSelector = function(state) {
    return state.visibleRowsLookup;
};
/**
 * @category Filtering
 * @ignore - do not document.
 */ export var gridFilteredRowsLookupSelector = createSelector(gridFilterStateSelector, function(filterState) {
    return filterState.filteredRowsLookup;
});
/**
 * @category Filtering
 * @ignore - do not document.
 */ export var gridFilteredChildrenCountLookupSelector = createSelector(gridFilterStateSelector, function(filterState) {
    return filterState.filteredChildrenCountLookup;
});
/**
 * @category Filtering
 * @ignore - do not document.
 */ export var gridFilteredDescendantCountLookupSelector = createSelector(gridFilterStateSelector, function(filterState) {
    return filterState.filteredDescendantCountLookup;
});
/**
 * Get the id and the model of the rows accessible after the filtering process.
 * Does not contain the collapsed children.
 * @category Filtering
 */ export var gridExpandedSortedRowEntriesSelector = createSelectorMemoized(gridVisibleRowsLookupSelector, gridSortedRowEntriesSelector, gridRowMaximumTreeDepthSelector, gridFilterModelSelector, gridQuickFilterValuesSelector, function(visibleRowsLookup, sortedRows, maxDepth, filterModel, quickFilterValues) {
    if (maxDepth < 2 && !filterModel.items.length && !(quickFilterValues === null || quickFilterValues === void 0 ? void 0 : quickFilterValues.length)) {
        return sortedRows;
    }
    return sortedRows.filter(function(row) {
        return visibleRowsLookup[row.id] !== false;
    });
});
/**
 * Get the id of the rows accessible after the filtering process.
 * Does not contain the collapsed children.
 * @category Filtering
 */ export var gridExpandedSortedRowIdsSelector = createSelectorMemoized(gridExpandedSortedRowEntriesSelector, function(visibleSortedRowEntries) {
    return visibleSortedRowEntries.map(function(row) {
        return row.id;
    });
});
/**
 * Get the id and the model of the rows accessible after the filtering process.
 * Contains the collapsed children.
 * @category Filtering
 */ export var gridFilteredSortedRowEntriesSelector = createSelectorMemoized(gridFilteredRowsLookupSelector, gridSortedRowEntriesSelector, function(filteredRowsLookup, sortedRows) {
    return sortedRows.filter(function(row) {
        return filteredRowsLookup[row.id] !== false;
    });
});
/**
 * Get the id of the rows accessible after the filtering process.
 * Contains the collapsed children.
 * @category Filtering
 */ export var gridFilteredSortedRowIdsSelector = createSelectorMemoized(gridFilteredSortedRowEntriesSelector, function(filteredSortedRowEntries) {
    return filteredSortedRowEntries.map(function(row) {
        return row.id;
    });
});
/**
 * Get the ids to position in the current tree level lookup of the rows accessible after the filtering process.
 * Does not contain the collapsed children.
 * @category Filtering
 * @ignore - do not document.
 */ export var gridExpandedSortedRowTreeLevelPositionLookupSelector = createSelectorMemoized(gridExpandedSortedRowIdsSelector, gridRowTreeSelector, function(visibleSortedRowIds, rowTree) {
    var depthPositionCounter = {};
    var lastDepth = 0;
    return visibleSortedRowIds.reduce(function(acc, rowId) {
        var rowNode = rowTree[rowId];
        if (!depthPositionCounter[rowNode.depth]) {
            depthPositionCounter[rowNode.depth] = 0;
        }
        // going deeper in the tree should reset the counter
        // since it might have been used in some other branch at the same level, up in the tree
        // going back up should keep the counter and continue where it left off
        if (rowNode.depth > lastDepth) {
            depthPositionCounter[rowNode.depth] = 0;
        }
        lastDepth = rowNode.depth;
        depthPositionCounter[rowNode.depth] += 1;
        acc[rowId] = depthPositionCounter[rowNode.depth];
        return acc;
    }, {});
});
/**
 * Get the id and the model of the top level rows accessible after the filtering process.
 * @category Filtering
 */ export var gridFilteredSortedTopLevelRowEntriesSelector = createSelectorMemoized(gridExpandedSortedRowEntriesSelector, gridRowTreeSelector, gridRowMaximumTreeDepthSelector, function(visibleSortedRows, rowTree, rowTreeDepth) {
    if (rowTreeDepth < 2) {
        return visibleSortedRows;
    }
    return visibleSortedRows.filter(function(row) {
        var _rowTree_row_id;
        return ((_rowTree_row_id = rowTree[row.id]) === null || _rowTree_row_id === void 0 ? void 0 : _rowTree_row_id.depth) === 0;
    });
});
/**
 * Get the amount of rows accessible after the filtering process.
 * @category Filtering
 */ export var gridExpandedRowCountSelector = createSelector(gridExpandedSortedRowEntriesSelector, function(visibleSortedRows) {
    return visibleSortedRows.length;
});
/**
 * Get the amount of top level rows accessible after the filtering process.
 * @category Filtering
 */ export var gridFilteredTopLevelRowCountSelector = createSelector(gridFilteredSortedTopLevelRowEntriesSelector, function(visibleSortedTopLevelRows) {
    return visibleSortedTopLevelRows.length;
});
/**
 * Get the amount of rows accessible after the filtering process.
 * Includes top level and descendant rows.
 * @category Filtering
 */ export var gridFilteredRowCountSelector = createSelector(gridFilteredSortedRowEntriesSelector, function(filteredSortedRowEntries) {
    return filteredSortedRowEntries.length;
});
/**
 * Get the amount of descendant rows accessible after the filtering process.
 * @category Filtering
 */ export var gridFilteredDescendantRowCountSelector = createSelector(gridFilteredRowCountSelector, gridFilteredTopLevelRowCountSelector, function(totalRowCount, topLevelRowCount) {
    return totalRowCount - topLevelRowCount;
});
/**
 * @category Filtering
 * @ignore - do not document.
 */ export var gridFilterActiveItemsSelector = createSelectorMemoized(gridFilterModelSelector, gridColumnLookupSelector, function(filterModel, columnLookup) {
    var _filterModel_items;
    return (_filterModel_items = filterModel.items) === null || _filterModel_items === void 0 ? void 0 : _filterModel_items.filter(function(item) {
        var _column_filterOperators, _item_value;
        if (!item.field) {
            return false;
        }
        var column = columnLookup[item.field];
        if (!(column === null || column === void 0 ? void 0 : column.filterOperators) || (column === null || column === void 0 ? void 0 : (_column_filterOperators = column.filterOperators) === null || _column_filterOperators === void 0 ? void 0 : _column_filterOperators.length) === 0) {
            return false;
        }
        var filterOperator = column.filterOperators.find(function(operator) {
            return operator.value === item.operator;
        });
        if (!filterOperator) {
            return false;
        }
        return !filterOperator.InputComponent || item.value != null && ((_item_value = item.value) === null || _item_value === void 0 ? void 0 : _item_value.toString()) !== '';
    });
});
/**
 * @category Filtering
 * @ignore - do not document.
 */ export var gridFilterActiveItemsLookupSelector = createSelectorMemoized(gridFilterActiveItemsSelector, function(activeFilters) {
    var result = activeFilters.reduce(function(res, filterItem) {
        if (!res[filterItem.field]) {
            res[filterItem.field] = [
                filterItem
            ];
        } else {
            res[filterItem.field].push(filterItem);
        }
        return res;
    }, {});
    return result;
});
